"use client";

import { DailyLog } from "@/types";
import { fetchLogs } from "@/utils/storage";
import { formatDateDisplay, getTodayISO } from "@/utils/date";
import { calculateDailyScore, calculateDailyProtein } from "@/utils/scoring";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Calendar, ChevronRight, Moon, Zap } from "lucide-react";
import clsx from "clsx";
import DayScore from "./DayScore";

const getSleepHours = (sleep: number | { hours: number; quality?: number }) => {
    return typeof sleep === 'object' ? sleep.hours : sleep;
};

export default function History() {
    const [logs, setLogs] = useState<DailyLog[]>([]);
    const [selectedLog, setSelectedLog] = useState<DailyLog | null>(null);

    useEffect(() => {
        const loadLogs = async () => {
            const loadedLogs = await fetchLogs();
            const today = getTodayISO();
            // Ensure newest logs appear first AND filter out future dates
            const sorted = [...loadedLogs]
                .filter(l => l.date <= today)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            setLogs(sorted);
        };
        loadLogs();
    }, []);

    if (logs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-text-muted">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <Calendar size={24} className="text-text-muted" />
                </div>
                <p className="text-xs font-medium">No history yet.</p>
                <p className="text-[10px] mt-1 opacity-60">Start logging your days!</p>
            </div>
        );
    }

    return (
        <div className="pb-20 relative min-h-[500px]">
            <AnimatePresence mode="wait">
                {selectedLog ? (
                    <motion.div
                        key="details"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="flex flex-col gap-4"
                    >
                        <div className="flex justify-between items-center mb-2">
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="flex items-center gap-2 text-xs text-text-muted hover:text-white transition-colors group"
                            >
                                <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                                    <ArrowLeft size={12} />
                                </div>
                                Back to History
                            </button>
                        </div>

                        <DayScore log={selectedLog} analysis={selectedLog.aiAnalysis || null} loading={false} />
                    </motion.div>
                ) : (
                    <motion.div
                        key="list"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xs font-bold uppercase tracking-widest text-text-muted">Past Logs</h2>
                            <div className="text-[10px] text-gray-500">{logs.length} entries</div>
                        </div>

                        {logs.map((log, i) => {
                            // Use live calculation to ensure consistency with Daily Input logic
                            const rawScore = calculateDailyScore(log);
                            const score = Math.min(100, rawScore);
                            return (
                                <div
                                    key={i}
                                    onClick={() => setSelectedLog(log)}
                                    className="glass-card flex items-center justify-between p-4 hover:bg-white/5 active:scale-[0.98] transition-all cursor-pointer group"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={clsx(
                                            "w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold border",
                                            score >= 80 ? "bg-green-500/10 border-green-500/20 text-green-500" :
                                                score >= 60 ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-500" :
                                                    "bg-red-500/10 border-red-500/20 text-red-500"
                                        )}>
                                            {score}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-white mb-0.5">{formatDateDisplay(log.date)}</div>
                                            <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                                <span className="flex items-center gap-1"><Zap size={10} /> {Math.round(calculateDailyProtein(log))}g</span>
                                                <span className="w-1 h-1 rounded-full bg-white/20"></span>
                                                <span className="flex items-center gap-1"><Moon size={10} /> {getSleepHours(log.sleep)}h</span>
                                                <span className="w-1 h-1 rounded-full bg-white/20"></span>
                                                <span>{log.workouts && log.workouts.length > 0 ? (log.workouts.length === 1 ? log.workouts[0].type : `${log.workouts.length} Sessions`) : "Rest"}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <ChevronRight size={16} className="text-gray-600 group-hover:text-white transition-colors" />
                                </div>
                            );
                        })}
                    </motion.div>
                )
                }
            </AnimatePresence>
        </div>
    );
}
